import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2, BookOpen, CheckCircle2, XCircle, Link2, Unlink, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function QuickBooksSettings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ client_id: "", client_secret: "", redirect_uri: "", environment: "sandbox" });
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ code: "", realmId: "", state: "" });

  const load = () =>
    api.get("/admin/quickbooks/settings").then(({ data }) => {
      setCfg(data);
      setF({ client_id: data.client_id || "", client_secret: "", redirect_uri: data.redirect_uri || "", environment: data.environment || "sandbox" });
    }).catch(() => setCfg(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("qb") === "connected") {
      toast.success("QuickBooks connected successfully.");
      window.history.replaceState({}, "", "/app/admin/quickbooks");
      load();
    } else if (p.get("qb_error")) {
      toast.error(p.get("qb_error"));
      window.history.replaceState({}, "", "/app/admin/quickbooks");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { client_id: f.client_id, redirect_uri: f.redirect_uri, environment: f.environment };
      if (f.client_secret.trim()) body.client_secret = f.client_secret.trim();
      await api.put("/admin/quickbooks/settings", body);
      toast.success("QuickBooks settings saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setBusy(false);
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/quickbooks/connect");
      if (data.url) { window.open(data.url, "_blank"); setShowManual(true); toast.message("Authorize in the new tab, then paste the callback values below."); }
    } catch (e) { toast.error(e.response?.data?.detail || "Configure credentials first"); }
    setBusy(false);
  };

  const completeConnect = async () => {
    setBusy(true);
    try {
      await api.post("/admin/quickbooks/callback", { code: manual.code.trim(), realmId: manual.realmId.trim(), state: manual.state.trim() });
      toast.success("QuickBooks connected"); setShowManual(false); setManual({ code: "", realmId: "", state: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); }
    setBusy(false);
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/admin/quickbooks/disconnect"); toast.success("Disconnected from QuickBooks"); load(); }
    catch (e) { toast.error("Failed to disconnect"); }
    setBusy(false);
  };

  if (cfg === false) return <DashboardLayout nav={getNav(user.role)} title="QuickBooks"><p>Unable to load.</p></DashboardLayout>;
  const connected = cfg?.connected;
  const enabled = cfg?.enabled;

  return (
    <DashboardLayout nav={getNav(user.role)} title="QuickBooks Online">
      {!cfg ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="max-w-2xl space-y-6">
          <div data-testid="qb-status-card" className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-between">
            <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-600" /><div><h2 className="font-head font-semibold text-slate-900">QuickBooks Online</h2><p className="text-xs text-slate-500">{cfg.environment === "production" ? "Production" : "Sandbox"} environment{cfg.realm_id ? ` · Realm ${cfg.realm_id}` : ""}</p></div></div>
            <span data-testid="qb-status-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{connected ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{connected ? "Connected" : "Not Connected"}</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-head font-semibold text-slate-900">OAuth 2.0 Credentials</h3>
            <div><Label>Client ID</Label><Input data-testid="qb-client-id" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className="mt-1.5" placeholder="Intuit app Client ID" /></div>
            <div><Label>Client Secret</Label><PasswordInput data-testid="qb-client-secret" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} className="mt-1.5" placeholder={cfg.masked_client_secret || "Intuit app Client Secret"} /><p className="mt-1 text-xs text-slate-400">Stored on the backend; never shown after saving ({cfg.masked_client_secret || "not set"}).</p></div>
            <div><Label>Redirect URI</Label><Input data-testid="qb-redirect-uri" value={f.redirect_uri} onChange={(e) => setF({ ...f, redirect_uri: e.target.value })} className="mt-1.5" placeholder="https://your-app/api/admin/quickbooks/callback" /><p className="mt-1 text-xs text-slate-400">Must exactly match a Redirect URI registered in your Intuit app.</p></div>
            <div>
              <Label>Environment</Label>
              <div className="mt-1.5 flex gap-2">
                {["sandbox", "production"].map((env) => (
                  <button key={env} data-testid={`qb-env-${env}`} type="button" onClick={() => setF({ ...f, environment: env })} className={`px-4 py-2 rounded-lg text-sm font-medium border ${f.environment === env ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{env === "sandbox" ? "Sandbox" : "Production"}</button>
                ))}
              </div>
            </div>
            <Button data-testid="qb-save-btn" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Settings"}</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-head font-semibold text-slate-900">Connection</h3>
            <p className="text-sm text-slate-500">SJ Property Care always remains the source of truth. When QuickBooks is not connected, records stay <span className="font-medium">Not Synced / Queued</span> and no operation is ever blocked.</p>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="qb-connect-btn" onClick={connect} disabled={busy || !enabled} className="bg-emerald-600 hover:bg-emerald-700"><Link2 className="w-4 h-4 mr-1.5" /> {connected ? "Reconnect" : "Connect to QuickBooks"}</Button>
              <Button data-testid="qb-disconnect-btn" onClick={disconnect} disabled={busy || !connected} variant="outline"><Unlink className="w-4 h-4 mr-1.5" /> Disconnect</Button>
            </div>
            {!enabled && <p className="text-xs text-amber-600">Save your Client ID and Client Secret before connecting.</p>}

            {showManual && (
              <div data-testid="qb-manual-callback" className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
                <p className="text-xs text-slate-600 flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> After authorizing, Intuit redirects with <code>code</code>, <code>realmId</code> &amp; <code>state</code>. Paste them here to finish connecting.</p>
                <div className="grid grid-cols-1 gap-2">
                  <Input data-testid="qb-cb-code" value={manual.code} onChange={(e) => setManual({ ...manual, code: e.target.value })} placeholder="Authorization code" />
                  <Input data-testid="qb-cb-realm" value={manual.realmId} onChange={(e) => setManual({ ...manual, realmId: e.target.value })} placeholder="realmId (Company ID)" />
                  <Input data-testid="qb-cb-state" value={manual.state} onChange={(e) => setManual({ ...manual, state: e.target.value })} placeholder="state" />
                </div>
                <Button data-testid="qb-complete-connect-btn" onClick={completeConnect} disabled={busy || !manual.code || !manual.realmId} size="sm" className="bg-blue-600 hover:bg-blue-700">Complete Connection</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
